import { USER_EVENTS } from '@shared/core/user-events'
import { GetProfileResponseDto } from '@shared/core/users'
import { useCallback, useEffect, useRef, useState } from 'react'
import { AutoReminderModal } from 'src/modules/alerts/auto-reminder-modal'
import { useAutoReminderModal } from 'src/modules/alerts/auto-reminder-modal/use-auto-reminder-modal'
import { InstallChromeExtensionModal } from 'src/modules/alerts/install-chrome-extension-modal/install-chrome-extension-modal'
import { useHasUserEvent } from 'src/modules/user-events/use-has-user-event'
import { useProfile } from 'src/modules/user/hooks/use-profile'
import classes from './dashboard-callout.module.scss'

const SLIDE_DURATION_MS = 10_000

// Slide action types
type SlideAction =
  | { type: 'open_auto_reminder_modal' }
  | { type: 'open_timezone_settings' }
  | { type: 'open_chrome_extension_modal' }

// Slide configuration type
interface CalloutSlide {
  id: string
  icon: string
  iconAlt: string
  title: string
  text: string
  linkText?: string
  action?: SlideAction
  isVisible: (profile: GetProfileResponseDto) => boolean
}

// Slide configurations
const SLIDES: CalloutSlide[] = [
  {
    id: 'install-extension',
    icon: 'https://firebasestorage.googleapis.com/v0/b/recordatorio-de-cita.appspot.com/o/did-you-know-question.svg?alt=media&token=2a2d501e-ea31-43d2-aab4-5602ebd2cb92',
    iconAlt: 'Chrome extension icon',
    title: 'Chrome extension',
    text: 'Install it to set reminders without leaving Google Calendar.',
    linkText: 'Learn more',
    action: { type: 'open_chrome_extension_modal' },
    isVisible: (profile: GetProfileResponseDto) => true, // Always visible for now
  },
  {
    id: 'calendar-reminders',
    icon: 'https://firebasestorage.googleapis.com/v0/b/recordatorio-de-cita.appspot.com/o/did-you-know-question.svg?alt=media&token=2a2d501e-ea31-43d2-aab4-5602ebd2cb92',
    iconAlt: 'Question mark',
    title: 'Did you know?',
    text: 'You can set reminders from any calendar app, including your phone?',
    linkText: 'Learn more',
    action: { type: 'open_auto_reminder_modal' },
    isVisible: (_profile: GetProfileResponseDto) => true, // Always visible for now
  },
]

export function DashboardCallout() {
  const profile = useProfile()
  const isDeactivated = useHasUserEvent(USER_EVENTS.AUTO_REMINDERS_CALLOUT_DEACTIVATED)
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  // Modal state management
  const { isModalOpen: isAutoReminderModalOpen, setModalOpen: setAutoReminderModalOpen } = useAutoReminderModal()
  const [isChromeExtensionModalOpen, setIsChromeExtensionModalOpen] = useState(false)

  // Filter visible slides based on profile data
  const visibleSlides = profile.data ? SLIDES.filter((slide) => slide.isVisible(profile.data)) : []

  // Start/restart the auto-advance interval
  const startAutoAdvance = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    if (visibleSlides.length <= 1) return

    intervalRef.current = setInterval(() => {
      setCurrentSlideIndex((prevIndex) => (prevIndex + 1) % visibleSlides.length)
    }, SLIDE_DURATION_MS)
  }, [visibleSlides.length])

  // Reset slide index when visible slides change
  useEffect(() => {
    setCurrentSlideIndex(0)
  }, [visibleSlides.length])

  // Auto-advance carousel
  useEffect(() => {
    startAutoAdvance()
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)

        intervalRef.current = null
      }
    }
  }, [startAutoAdvance])

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  // Handle manual slide change and reset timeout
  const handleSlideChange = (newIndex: number) => {
    setCurrentSlideIndex(newIndex)
    startAutoAdvance() // Reset the timeout
  }

  // Action handlers for slide actions
  const handleSlideAction = (action: SlideAction) => {
    switch (action.type) {
      case 'open_auto_reminder_modal':
        setAutoReminderModalOpen(true)
        break

      case 'open_chrome_extension_modal':
        setIsChromeExtensionModalOpen(true)
        break

      default:
        console.warn('Unknown slide action:', action)
    }
  }

  // Handle slide link clicks

  const handleSlideLinkClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, action?: SlideAction) => {
    e.preventDefault()
    if (action) {
      handleSlideAction(action)
    }
  }

  if (profile.isLoading || profile.data?.usage_mode !== 'customer') {
    return null
  }

  if (isDeactivated.isLoading || isDeactivated.data) {
    return null
  }

  if (visibleSlides.length === 0) {
    return null
  }

  const currentSlide = visibleSlides[currentSlideIndex]

  return (
    <div className={classes.callout}>
      <div className={classes.slideContainer}>
        <h4>
          <img src={currentSlide.icon} alt={currentSlide.iconAlt} aria-hidden />

          {currentSlide.title}
        </h4>

        <p>{currentSlide.text}</p>

        {currentSlide.linkText && (
          <p>
            <a href="#" onClick={(e) => handleSlideLinkClick(e, currentSlide.action)}>
              {currentSlide.linkText}
            </a>
          </p>
        )}
      </div>

      {/* Slide indicators */}

      {visibleSlides.length > 1 && (
        <div className={classes.indicators}>
          {visibleSlides.map((_, index) => (
            <button
              key={index}
              className={`${classes.indicator} ${index === currentSlideIndex ? classes.active : ''}`}
              onClick={() => handleSlideChange(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Modals */}

      {isChromeExtensionModalOpen && (
        <InstallChromeExtensionModal
          open={isChromeExtensionModalOpen}
          onClose={() => setIsChromeExtensionModalOpen(false)}
        />
      )}

      {isAutoReminderModalOpen && (
        <AutoReminderModal open={isAutoReminderModalOpen} onClose={() => setAutoReminderModalOpen(false)} />
      )}

      {/* TODO: Add phone numbers modal when available */}
    </div>
  )
}
